import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useAuth, useTheme } from '../hooks';

/**
 * Troca de senha. Obrigatória no 1º acesso (precisa_trocar_senha), e também
 * acessível voluntariamente em /trocar-senha.
 */
export function TrocarSenha() {
  const { login, marcarSenhaTrocada, logout } = useAuth();
  const { tema, toggle } = useTheme();
  const navigate = useNavigate();

  const obrigatoria = !!login?.precisa_trocar_senha;
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirma, setConfirma] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [carregando, setCarregando] = useState(false);

  // Sem sessão local → volta pro login.
  if (!login) {
    navigate('/login', { replace: true });
    return null;
  }

  async function salvar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    if (nova.length < 6) return setErro('A nova senha deve ter ao menos 6 caracteres.');
    if (nova !== confirma) return setErro('A confirmação não confere.');
    setCarregando(true);
    try {
      await api.trocarSenha(atual, nova);
      marcarSenhaTrocada();
      setOk(true);
      setTimeout(() => navigate('/semanal', { replace: true }), 800);
    } catch (err) {
      setErro(err instanceof ApiError && err.status === 403
        ? 'Senha atual incorreta.'
        : err instanceof ApiError
          ? err.message.replace(/^API \d+:\s*/, '')
          : 'Falha ao trocar a senha.');
    } finally {
      setCarregando(false);
    }
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 via-white to-blue-100 dark:from-gray-900 dark:via-gray-900 dark:to-blue-950 min-h-screen flex items-center justify-center p-4 text-gray-800 dark:text-gray-100">
      <button
        onClick={toggle}
        className="fixed top-4 right-4 p-2 bg-white dark:bg-gray-800 rounded-full shadow hover:shadow-md text-gray-600 dark:text-gray-300"
      >
        {tema === 'dark' ? '☀️' : '🌙'}
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-700 flex items-center justify-center text-white text-2xl mb-3">🔑</div>
          <h1 className="text-xl font-bold">{obrigatoria ? 'Defina sua nova senha' : 'Trocar senha'}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {obrigatoria
              ? `Senha resetada pelo administrador. Defina uma senha pessoal para ${login.nome} continuar.`
              : `Usuário: ${login.nome}`}
          </p>
        </div>

        <form onSubmit={salvar} className="space-y-3">
          {!obrigatoria && (
          <input
            type="password" value={atual} onChange={(e) => setAtual(e.target.value)}
            autoComplete="current-password" placeholder="Senha atual"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          )}
          <input
            type="password" value={nova} onChange={(e) => setNova(e.target.value)}
            autoComplete="new-password" placeholder="Nova senha (≥ 6 caracteres)"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="password" value={confirma} onChange={(e) => setConfirma(e.target.value)}
            autoComplete="new-password" placeholder="Confirme a nova senha"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {erro && (
            <div className="text-xs text-red-600 dark:text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">⚠ {erro}</div>
          )}
          {ok && (
            <div className="text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">✓ Senha alterada — entrando…</div>
          )}

          <button
            type="submit" disabled={carregando || ok}
            className="w-full py-2.5 bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white rounded-lg font-medium text-sm transition flex items-center justify-center gap-2"
          >
            {carregando ? <span className="animate-spin">⏳</span> : null}
            {carregando ? 'Salvando…' : 'Salvar nova senha'}
          </button>
        </form>

        <button
          onClick={() => { logout(); navigate('/login', { replace: true }); }}
          className="mt-4 w-full text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          Sair
        </button>
      </div>
    </div>
  );
}
